import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { mdiCellphone, mdiEmailOutline, mdiPencilOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog'
import { AssetPicker, isAssetPickerEscapeToClear } from '../AssetPicker'
import { ContactPicker } from './ContactPicker'
import { useWhatsAppTemplates } from './WhatsAppTemplatePicker'

/**
 * "Write to a contact" — the one door of the ledger that opens with the manager
 * speaking (spec 2026-09-15-01).
 *
 * Everything else in the queue exists because a message ARRIVED. This dialog is
 * the mirror: a manager writes to a party they can name, and because the thread
 * is created around it, the answer lands back in the ledger instead of in one
 * person's mailbox.
 *
 * It SELECTS; it does not compose (spec 2026-09-16-10). Contact, site, subject
 * and the medium — the three things the request names, plus the address — and
 * then *Start* opens the thread and drops the manager into the panel, where the
 * first message is written in the standard composer with the autosaved draft,
 * the previous-draft line, the attachments and the medium switch all working. A
 * modal textarea had none of that, and a second surface that composes a message
 * only drifts from the first. The ordering is what makes it possible at all: a
 * draft is keyed on a conversation uid, so the thread has to exist first.
 *
 * Two things it deliberately does NOT do:
 *
 *   - offer a free-text recipient. The address is picked from the CONTACT's own
 *     mediums (`GET …/conversations/contact-channels`), which is the same list
 *     the server validates against — that is what keeps the thread's contact
 *     honest, and it is why a contact with no address disables *Start* rather
 *     than opening a text field. The panel's « Rattacher une adresse » is the
 *     recovery path, but a thread that cannot be answered must not be openable
 *     in one click;
 *   - pre-fill the composer. An empty composer is the point; were that ever to
 *     change it goes through `ComposerApi.applyDraft` and the consume-once
 *     search param of spec 2026-09-16-09, never by writing the draft row.
 */

/** One medium of a contact, as the API returns it. */
type Candidate = { channel: string; identity: string; source: string }

export function NewConversationButton({
    org,
    contactUserUid,
    contactName,
    assetUid,
    assetName,
    variant = 'default',
    size = 'default',
    className,
    testId = 'conversation-new',
    label,
    icon = mdiPencilOutline,
}: {
    org: string
    /** Pre-selects the contact — the contact page's entry point. */
    contactUserUid?: string
    contactName?: string
    /** Pre-anchors the site — the asset page's entry point. */
    assetUid?: string
    assetName?: string
    variant?: 'default' | 'outline'
    /** `default` in a page header (patterns §5); `sm` in a card or a row. */
    size?: 'default' | 'sm'
    className?: string
    testId?: string
    /** Overrides the default « New conversation » wording. */
    label?: string
    icon?: string
}) {
    const { t } = useTranslation('conversations')
    const [open, setOpen] = useState(false)
    const text = label ?? t('start.action')

    return (
        <>
            <Button
                variant={variant}
                size={size}
                className={className}
                onClick={() => setOpen(true)}
                aria-label={text}
                title={text}
                data-testid={testId}
            >
                <AppIcon path={icon} size="xs" className="md:mr-1" />
                <span className="hidden md:inline">{text}</span>
            </Button>
            {open && (
                <NewConversationDialog
                    org={org}
                    contactUserUid={contactUserUid}
                    contactName={contactName}
                    assetUid={assetUid}
                    assetName={assetName}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    )
}

function NewConversationDialog({
    org,
    contactUserUid,
    contactName,
    assetUid,
    assetName,
    onClose,
}: {
    org: string
    contactUserUid?: string
    contactName?: string
    assetUid?: string
    assetName?: string
    onClose: () => void
}) {
    const { t } = useTranslation('conversations')
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const [contact, setContact] = useState<{ uid: string; name: string } | null>(
        contactUserUid ? { uid: contactUserUid, name: contactName ?? '' } : null,
    )
    const [asset, setAsset] = useState<{ uid: string; name: string } | null>(
        assetUid ? { uid: assetUid, name: assetName ?? '' } : null,
    )
    const [selected, setSelected] = useState<Candidate | null>(null)
    const [existingUid, setExistingUid] = useState<string | null>(null)
    const [subject, setSubject] = useState('')
    const [error, setError] = useState<string | null>(null)

    const { data: channels, isLoading: channelsLoading } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/conversations/contact-channels',
        {
            params: { path: { orgCode: org }, query: { contact: contact?.uid ?? '' } },
        },
        { enabled: Boolean(contact?.uid) },
    )

    // E-mail addresses and WhatsApp numbers of the contact (spec
    // 2026-10-26-04). A WhatsApp thread has no inbound, so its first message is
    // the "continue on the web" template, sent from the composer.
    // The template catalogue answers 503 when the deployment has no WhatsApp
    // sender: WhatsApp is offered only once it answered.
    const whatsappAvailable = useWhatsAppTemplates(org, true).data !== undefined
    const addresses = useMemo(
        () =>
            ((channels?.candidates ?? []) as Candidate[]).filter(
                (c) => c.channel === 'email' || (c.channel === 'whatsapp' && whatsappAvailable),
            ),
        [channels, whatsappAvailable],
    )

    // The default is the first entry: the contact's primary address, the same
    // rule the server applies when `identity` is omitted.
    useEffect(() => {
        setSelected(addresses.length > 0 ? addresses[0] : null)
    }, [addresses])

    const isSelected = (c: Candidate) => c.channel === selected?.channel && c.identity === selected?.identity

    const openThread = useCallback(
        (uid: string) => {
            onClose()
            navigate({
                to: '/$org/conversations',
                params: { org },
                search: {
                    conversation: uid,
                    status: 'open,waiting_resident,closed',
                    channel: undefined,
                    resolution: undefined,
                    asset: undefined,
                    unattached: undefined,
                    draft: undefined,
                    q: undefined,
                    selected: undefined,
                    reply_for_issue: undefined,
                    message: undefined,
                },
            })
        },
        [navigate, onClose, org],
    )

    const start = $api.useMutation('post', '/api/v1/organizations/{orgCode}/conversations', {
        onSuccess: (created: unknown) => {
            const uid = (created as { uid?: string } | undefined)?.uid
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey as unknown[]

                    return key[0] === 'get' && key[1] === '/api/v1/organizations/{orgCode}/conversations'
                },
            })
            // Land on the thread itself: the composer is where the first
            // message gets written, and it is empty and focused there.
            if (uid) openThread(uid)
            else onClose()
        },
        onError: (res: unknown) => {
            const payload = res as { title?: string; code?: string; detail?: string } | undefined
            const text = `${payload?.detail ?? ''} ${payload?.title ?? ''}`
            const existing = payload?.code === 'CONFLICT' ? text.match(/[0-9a-f]{8}-[0-9a-f-]{27}/i)?.[0] : undefined
            if (existing) {
                setExistingUid(existing)
                setError(t('start.whatsapp_thread_exists'))

                return
            }
            setError(
                payload?.code === 'NO_REACHABLE_ADDRESS'
                    ? t('start.no_address')
                    : payload?.code === 'WHATSAPP_UNAVAILABLE'
                      ? t('errors.whatsapp_unavailable')
                      : (payload?.title ?? t('start.error')),
            )
        },
    })

    const noAddress = Boolean(contact) && !channelsLoading && addresses.length === 0
    const canStart =
        Boolean(contact?.uid) && selected !== null && subject.trim() !== '' && !start.isPending

    // No `body`: the server's start shape takes none since spec 2026-09-16-10,
    // and the thread is opened message-less on purpose.
    const startThread = useCallback(() => {
        if (!contact?.uid || !selected) return
        setError(null)
        setExistingUid(null)
        start.mutate({
            params: { path: { orgCode: org } },
            body: {
                contact_user_uid: contact.uid,
                channel: selected.channel as 'email' | 'whatsapp',
                identity: selected.identity,
                subject: subject.trim(),
                ...(asset?.uid ? { asset_uid: asset.uid } : {}),
            },
        })
    }, [asset, contact, selected, org, start, subject])

    return (
        <Dialog open onOpenChange={(next) => !next && onClose()}>
            <DialogContent
                className="sm:max-w-lg"
                data-testid="conversation-new-dialog"
                onEscapeKeyDown={(event) => {
                    if (isAssetPickerEscapeToClear(event)) event.preventDefault()
                }}
            >
                <DialogHeader>
                    <DialogTitle>{t('start.title')}</DialogTitle>
                    <DialogDescription>{t('start.description')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label>{t('start.contact')}</Label>
                        {contact ? (
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-foreground" data-testid="conversation-new-contact">
                                    {contact.name || contact.uid}
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => setContact(null)}
                                    data-testid="conversation-new-contact-change"
                                >
                                    {t('contact.change')}
                                </Button>
                            </div>
                        ) : (
                            <ContactPicker
                                org={org}
                                onChange={(uid, name) => setContact({ uid, name })}
                            />
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <Label>{t('start.medium')}</Label>
                        {noAddress && (
                            <p className="text-xs text-destructive" data-testid="conversation-new-no-address">
                                {t('start.no_address')}
                            </p>
                        )}
                        {addresses.length > 0 && (
                            <ul className="divide-y rounded-md border" data-testid="conversation-new-addresses">
                                {addresses.map((candidate) => (
                                    <li key={`${candidate.channel}|${candidate.identity}`}>
                                        <button
                                            type="button"
                                            className={`flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-muted/60 ${
                                                isSelected(candidate) ? 'bg-muted font-medium' : ''
                                            }`}
                                            aria-pressed={isSelected(candidate)}
                                            onClick={() => setSelected(candidate)}
                                            data-testid={`conversation-new-address-${candidate.identity}`}
                                        >
                                            <span data-testid={`conversation-new-channel-${candidate.channel}`} className="flex items-center gap-1 text-muted-foreground">
                                                <AppIcon
                                                    path={candidate.channel === 'whatsapp' ? mdiCellphone : mdiEmailOutline}
                                                    size="xs"
                                                    aria-hidden
                                                />
                                                {t(`channel.${candidate.channel}`)}
                                            </span>
                                            <span className="truncate text-foreground">{candidate.identity}</span>
                                            <span className="ml-auto text-[10px] text-muted-foreground">
                                                {t(`contact.medium_source_${candidate.source}`, {
                                                    defaultValue: candidate.source,
                                                })}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <Label>{t('start.asset')}</Label>
                        {asset ? (
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-foreground" data-testid="conversation-new-asset">
                                    {asset.name || asset.uid}
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => setAsset(null)}
                                    data-testid="conversation-new-asset-clear"
                                >
                                    {t('actions.remove_asset')}
                                </Button>
                            </div>
                        ) : (
                            <AssetPicker
                                org={org}
                                testId="conversation-new-asset-picker"
                                scopeUid={assetUid}
                                scopeLabel={assetName}
                                help={t('start.asset_help')}
                                onChange={(uid, name) => setAsset({ uid, name })}
                            />
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="conversation-new-subject">{t('actions.reply_subject')}</Label>
                        <Input
                            id="conversation-new-subject"
                            value={subject}
                            onChange={(event) => setSubject(event.target.value)}
                            data-testid="conversation-new-subject"
                        />
                    </div>

                    {error && (
                        <p className="text-xs text-destructive" data-testid="conversation-new-error">
                            {error}
                            {existingUid && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="h-auto px-1 py-0 text-xs"
                                    onClick={() => openThread(existingUid)}
                                    data-testid="conversation-new-open-existing"
                                >
                                    {t('start.open_existing')}
                                </Button>
                            )}
                        </p>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="conversation-new-cancel">
                        {t('contact.medium_cancel')}
                    </Button>
                    {/* Disabled while the request is in flight: that IS the
                        double-submit guard (spec § A.4) — no idempotency key,
                        with the thread's own 24 h cap as the backstop. */}
                    <Button onClick={startThread} disabled={!canStart} data-testid="conversation-new-start">
                        {t('start.start')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
